/**
 * Team Files Service
 * Handles team file upload, listing, and management
 */

import { TeamScopedService } from './base.service';
import { authService } from './auth/authService';

/**
 * Team file list item (from API)
 */
export interface TeamFileListItem {
  id: string;
  name: string;
  slug: string;
  mimeType: string;
  sizeBytes: number;
  status: 'pending' | 'processing' | 'ready' | 'failed';
  alwaysInContext: boolean;
  uploadedAt: string;
  uploadedBy: string;
}

/**
 * Full team file entity (from API)
 */
export interface TeamFileEntity {
  entity: 'file';
  connectorName: 'team-files';
  id: string;
  name: string;
  searchText: string;
  summary: string;
  originalPath: string;
  mimeType: string;
  sizeBytes: number;
  status: 'pending' | 'processing' | 'ready' | 'failed';
  errorMessage?: string;
  alwaysInContext: boolean;
  uploadedAt: string;
  uploadedBy: string;
}

/**
 * Upload response
 */
export interface UploadFileResponse {
  success: boolean;
  data?: {
    id: string;
    name: string;
    slug: string;
    summary: string;
    searchText: string;
    mimeType: string;
    sizeBytes: number;
    status: string;
    uploadedAt: string;
  };
  error?: string;
  message?: string;
}

/**
 * Context usage stats
 */
export interface ContextUsage {
  files: number;
  tokens: number;
  maxFiles: number;
  maxTokens: number;
  warning?: string;
}

/**
 * List files response
 */
export interface ListFilesResponse {
  success: boolean;
  data?: {
    files: TeamFileListItem[];
  };
  contextUsage?: ContextUsage;
  error?: string;
}

/**
 * Get file response
 */
export interface GetFileResponse {
  success: boolean;
  data?: TeamFileEntity;
  error?: string;
}

/**
 * Delete file response
 */
export interface DeleteFileResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Update file response
 */
export interface UpdateFileResponse {
  success: boolean;
  data?: TeamFileEntity;
  error?: string;
}

/**
 * Regenerate summary response
 */
export interface RegenerateSummaryResponse {
  success: boolean;
  data?: {
    status: string;
    summary: string;
    searchText: string;
    message: string;
  };
  error?: string;
  message?: string;
}

/**
 * File constraints (must match backend)
 */
export const FILE_CONSTRAINTS = {
  MAX_SIZE_BYTES: 1 * 1024 * 1024, // 1MB
  ALLOWED_EXTENSIONS: ['.md', '.txt'] as const,
  ALLOWED_MIME_TYPES: ['text/markdown', 'text/plain'] as const,
};

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api/v1';

class TeamFilesService extends TeamScopedService {
  /**
   * List all team files (with context usage stats)
   */
  async listFiles(teamId: string): Promise<{ files: TeamFileListItem[]; contextUsage?: ContextUsage }> {
    const response = await this.getTeam<ListFilesResponse>(teamId, '/knowledge/files');
    return {
      files: response.data?.files || [],
      contextUsage: response.contextUsage,
    };
  }

  /**
   * Get a specific file by ID
   */
  async getFile(teamId: string, fileId: string): Promise<TeamFileEntity | null> {
    const response = await this.getTeam<GetFileResponse>(teamId, `/knowledge/files/${encodeURIComponent(fileId)}`);
    return response.data || null;
  }

  /**
   * Upload a file
   * Uses FormData for multipart upload
   */
  async uploadFile(teamId: string, file: File, description?: string): Promise<UploadFileResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }

    const token = await authService.getToken();
    const url = `${API_BASE_URL}/teams/${teamId}/knowledge/files`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        // Note: Do NOT set Content-Type for FormData - browser sets it with boundary
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        error: `HTTP ${response.status}`,
      }));
      return {
        success: false,
        error: error.error || error.message || `Upload failed: ${response.status}`,
      };
    }

    return response.json();
  }

  /**
   * Delete a file
   */
  async deleteFile(teamId: string, fileId: string): Promise<DeleteFileResponse> {
    return this.deleteTeam<DeleteFileResponse>(teamId, `/knowledge/files/${encodeURIComponent(fileId)}`);
  }

  /**
   * Update file metadata (summary, alwaysInContext)
   */
  async updateFile(
    teamId: string,
    fileId: string,
    updates: { summary?: string; alwaysInContext?: boolean }
  ): Promise<UpdateFileResponse> {
    return this.patchTeam<UpdateFileResponse>(teamId, `/knowledge/files/${encodeURIComponent(fileId)}`, updates);
  }

  /**
   * Regenerate AI summary for a file
   */
  async regenerateSummary(teamId: string, fileId: string): Promise<RegenerateSummaryResponse> {
    return this.postTeam<RegenerateSummaryResponse>(
      teamId,
      `/knowledge/files/${encodeURIComponent(fileId)}/regenerate-summary`,
      {}
    );
  }

  /**
   * Get download URL for a file
   */
  getDownloadUrl(teamId: string, fileId: string): string {
    return `${API_BASE_URL}/teams/${teamId}/knowledge/files/${encodeURIComponent(fileId)}/content`;
  }

  /**
   * Download a file (triggers browser download)
   */
  async downloadFile(teamId: string, fileId: string, filename: string): Promise<void> {
    const token = await authService.getToken();
    const url = this.getDownloadUrl(teamId, fileId);

    const response = await fetch(url, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  /**
   * Validate a file before upload
   */
  validateFile(file: File): { valid: boolean; error?: string } {
    // Check file size
    if (file.size > FILE_CONSTRAINTS.MAX_SIZE_BYTES) {
      const maxSizeMB = FILE_CONSTRAINTS.MAX_SIZE_BYTES / (1024 * 1024);
      return {
        valid: false,
        error: `File too large. Maximum size: ${maxSizeMB}MB`,
      };
    }

    // Check file extension
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!FILE_CONSTRAINTS.ALLOWED_EXTENSIONS.includes(ext as any)) {
      return {
        valid: false,
        error: `Invalid file type. Allowed types: ${FILE_CONSTRAINTS.ALLOWED_EXTENSIONS.join(', ')}`,
      };
    }

    return { valid: true };
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}

export const teamFilesService = new TeamFilesService();
